import React, { useState, useEffect } from 'react';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Card from '../../components/common/Card/Card';
import { userSettingsApi, UserSettingsResponse } from '../../api/userSettings';
import Notification from '../../components/common/Notification/Notification';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<UserSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await userSettingsApi.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load settings. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMarketingEmails = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const updatedSettings = await userSettingsApi.updateSettings({
        marketing_emails: !settings.marketing_emails
      });
      setSettings(updatedSettings);
      setNotification({
        type: 'success',
        message: 'Settings updated successfully.'
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
      setNotification({
        type: 'error',
        message: 'Failed to update settings. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>

        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}

        <Card title="Email Preferences">
          <div className="flex items-center justify-between py-4">
            <div className="flex-1">
              <label htmlFor="marketing-emails" className="text-sm font-medium text-gray-700">
                Marketing Emails
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Receive emails about new features, tips, and special offers
              </p>
            </div>
            <button
              id="marketing-emails"
              type="button"
              onClick={handleToggleMarketingEmails}
              disabled={saving}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                ${settings?.marketing_emails ? 'bg-primary' : 'bg-gray-200'}
                ${saving ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              role="switch"
              aria-checked={settings?.marketing_emails}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                  transition duration-200 ease-in-out
                  ${settings?.marketing_emails ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
};

export default Settings;

